import { homePlanStatusValues } from "api-types/homePlans/homePlanTypes";
import type { HomePlanStatus } from "api-types/homePlans/homePlanTypes";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import { evaluateHomePlanCreationEligibility } from "api-types/locations/homePlanCreationEligibility";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { ClientLot, ClientLotHydrated } from "api-types/lots/lotsResponses";
import type { LotStatus } from "api-types/lots/lotTypes";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { isMasterPlanMultiUnit } from "lib/components/builders/communities/plans/planUtils";
import { getBuilderLocationPlansURL } from "lib/endpointHelpers";
import type { CommunityPlanLotInfo } from "./LotsTypes";

export const GARAGE_HANDING_MULTI_UNIT_TOOLTIP = "Garage handing not supported for lots in Multi-Unit locations";

export const MULTIPLE_HOME_PLANS_STATUS_TOOLTIP =
  "Status can't be edited when multiple Home Plans are assigned to a lot. Use the Home Plan details view to advance a plan.";

const ADVANCED_HOME_PLAN_PHASE_LABEL: Record<Exclude<HomePlanStatus, "PREP">, string> = {
  PRE_CONSTRUCTION: "Pre-Construction",
  CONSTRUCTION: "Construction",
};

type ConfiguredHomePlanForEditability = Readonly<{
  id: string;
  planStatus: HomePlanStatus;
  planName?: string | null;
}>;

export function getLotStatusEditability({
  configuredHomePlans,
}: {
  configuredHomePlans: ReadonlyArray<ConfiguredHomePlanForEditability> | undefined;
}): {
  allHomePlans: ReadonlyArray<ConfiguredHomePlanForEditability>;
  activeHomePlan: ConfiguredHomePlanForEditability | undefined;
  homePlanPhase: HomePlanStatus;
  hasMultipleHomePlans: boolean;
  isStatusEditable: boolean;
  isLotFieldsEditable: boolean;
  statusDisabledTooltip: string | undefined;
} {
  const allHomePlans = configuredHomePlans ?? [];
  const activeHomePlan = allHomePlans.find((homePlan) => homePlan.planStatus !== "PREP");
  const homePlanPhase = activeHomePlan?.planStatus ?? "PREP";
  const hasMultipleHomePlans = allHomePlans.length > 1;

  return {
    allHomePlans,
    activeHomePlan,
    homePlanPhase,
    hasMultipleHomePlans,
    isStatusEditable: homePlanPhase === "PREP" && !hasMultipleHomePlans,
    isLotFieldsEditable: homePlanPhase === "PREP",
    statusDisabledTooltip: getLotStatusDisabledTooltip({ hasMultipleHomePlans, homePlanPhase }),
  };
}

export function getLotStatusDisabledTooltip({
  hasMultipleHomePlans,
  homePlanPhase,
}: {
  hasMultipleHomePlans: boolean;
  homePlanPhase?: HomePlanStatus;
}): string | undefined {
  if (hasMultipleHomePlans) {
    return MULTIPLE_HOME_PLANS_STATUS_TOOLTIP;
  }
  // Unit home plans at multi-unit locations stay in PREP (lot status is the phase tracker),
  // so this branch only applies when a home plan has actually left Plan Preparation.
  if (homePlanPhase === "PRE_CONSTRUCTION" || homePlanPhase === "CONSTRUCTION") {
    return `Status cannot be edited during ${ADVANCED_HOME_PLAN_PHASE_LABEL[homePlanPhase]} phase.`;
  }
  return undefined;
}

// Location plans offered in the lot table "Plans Available" MultiSelect.
// The viewed location's own plans are gated on its multi-unit eligibility here; plans fetched
// from descendant locations keep their multi-unit metadata and are gated per lot instead
// (planOptionsAtLotLocation), since their location type can differ from the page's.
export function buildLotTableCommunityPlanOptions({
  locationPlans,
  masterPlans,
  location,
}: {
  locationPlans: ClientLocationPlan[] | undefined;
  masterPlans: ClientMasterPlan[] | undefined;
  location: ClientLocationWithLevel | undefined;
}): CommunityPlanLotInfo[] {
  if (!locationPlans) {
    return [];
  }

  const masterPlansById = Object.fromEntries(masterPlans?.map((mp) => [mp.id, mp]) ?? []);
  const atMultiUnitLocation = isMultiUnitLocation(location);
  const options: CommunityPlanLotInfo[] = [];

  for (const plan of [...locationPlans].sort((a, b) => a.name.localeCompare(b.name))) {
    if (!plan.locationId || !plan.masterPlanId) {
      continue;
    }
    const masterPlan = masterPlansById[plan.masterPlanId];
    const isMultiUnitPlan = isMasterPlanMultiUnit(masterPlan);
    if (plan.locationId === location?.id) {
      const eligibility = evaluateHomePlanCreationEligibility({
        atMultiUnitLocation,
        planIsMultiUnit: isMultiUnitPlan,
      });
      if (!eligibility.ok) {
        continue;
      }
    }
    options.push({
      value: plan.id ?? "",
      label: plan.name,
      masterPlanId: plan.masterPlanId,
      masterPlanName: masterPlan?.name ?? "",
      locationId: plan.locationId,
      isMultiUnitPlan,
    });
  }

  return options;
}

export function buildPlanNamesById(locationPlans: ClientLocationPlan[] | undefined): Map<string, string> {
  const namesById = new Map<string, string>();
  for (const plan of locationPlans ?? []) {
    if (plan.id) {
      namesById.set(plan.id, plan.name);
    }
  }
  return namesById;
}

/**
 * Sort key for a lot's "Plans Available" cell, derived from its current plan ids because the
 * `selectedLocationPlanNames` snapshot only refreshes on load. Sorted, so re-picking the same
 * plans in a different order does not move the row.
 */
export function getLotPlanNamesKey(
  allowedCommunityPlanIds: string[] | undefined,
  planNamesById: ReadonlyMap<string, string>,
): string {
  return (allowedCommunityPlanIds ?? [])
    .map((communityPlanId) => planNamesById.get(communityPlanId))
    .filter((name): name is string => !!name)
    .sort((a, b) => a.localeCompare(b, "en", { numeric: true }))
    .join(", ");
}

/** Options a lot's "Plans Available" select may offer: plans at the lot's own location that fit its location type. */
export function planOptionsAtLotLocation(
  options: CommunityPlanLotInfo[],
  lot: Pick<ClientLotHydrated, "communityId" | "locationLevelType">,
): CommunityPlanLotInfo[] {
  const atMultiUnitLocation = isLotInMultiUnitLocation(lot);
  return options.filter(
    (option) =>
      option.locationId === lot.communityId &&
      evaluateHomePlanCreationEligibility({ atMultiUnitLocation, planIsMultiUnit: option.isMultiUnitPlan }).ok,
  );
}

/**
 * Plans fetch for the lot tables. Detached plans live at each lot's own location, so a parent-level
 * view needs the whole subtree for lot plan ids to match.
 */
export function buildLotTablePlansURL({
  builderId,
  locationId,
  detached,
}: {
  builderId: string;
  locationId: string;
  detached: boolean;
}): string {
  return getBuilderLocationPlansURL({ builderId, locationId, detached, includeChildren: detached });
}

export function isLotInMultiUnitLocation(lot: Pick<ClientLotHydrated, "locationLevelType">): boolean {
  return lot.locationLevelType === "multiunit";
}

/**
 * Checks if a new home plan can be created on a given lot.
 * Only lots with status "available" and no active home plans are eligible.
 */
export function canCreateHomePlanOnLot(lot: ClientLotHydrated | null | undefined): boolean {
  if (!lot) {
    return false;
  }

  if (lot.lotStatus !== "available") {
    return false;
  }

  const hasActiveHomePlan = lot.configuredHomePlans?.some(
    (hp) => hp.planStatus === "PRE_CONSTRUCTION" || hp.planStatus === "CONSTRUCTION",
  );

  if (hasActiveHomePlan) {
    return false;
  }

  return true;
}

/**
 * Furthest-along home-plan status on a lot: CONSTRUCTION, then PRE_CONSTRUCTION, then PREP.
 * A CONSTRUCTION plan is always the lot's only home plan; PREP and PRE_CONSTRUCTION can coexist.
 */
export function getFurthestHomePlanStatus(homePlans: { planStatus: HomePlanStatus }[] | undefined): HomePlanStatus {
  let furthest: HomePlanStatus = "PREP";
  let furthestRank = 0;
  for (const plan of homePlans ?? []) {
    const rank = homePlanStatusValues.indexOf(plan.planStatus);
    if (rank > furthestRank) {
      furthest = plan.planStatus;
      furthestRank = rank;
    }
  }
  return furthest;
}

// Mirrors server-side validateLotIsMovable from lotService.ts
// Could consider trying to share between client and server, but kept separate for now
const IMMOVABLE_STATUSES: LotStatus[] = ["sold", "pending"];

/**
 * Everything eligibility looks at, mirroring the server's own `LotMoveCandidate`. Stated as counts
 * rather than the plans themselves, so callers can gate on the plan-count lot response.
 */
export type LotUnitAssignmentCandidate = Pick<ClientLot, "lotStatus" | "releasedAt" | "reportingUnitId"> & {
  homePlanCount: number;
  lotPlanCount: number;
};

export function getUnitAssignmentIneligibilityReason(lot: LotUnitAssignmentCandidate): string | null {
  if (lot.releasedAt) {
    return "released";
  }
  if (IMMOVABLE_STATUSES.includes(lot.lotStatus)) {
    return `status:${lot.lotStatus}`;
  }
  if (lot.reportingUnitId) {
    return "hasUnitAssignment";
  }
  if (lot.homePlanCount > 0) {
    return `hasHomePlans:${lot.homePlanCount}`;
  }
  if (lot.lotPlanCount > 0) {
    return `hasLotPlanAssignments:${lot.lotPlanCount}`;
  }
  return null;
}

export function isLotEligibleForUnitAssignment(lot: LotUnitAssignmentCandidate): boolean {
  return getUnitAssignmentIneligibilityReason(lot) === null;
}
